import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { RefreshCw, Monitor, Lock, Link, Copy, Trash2, Plus } from 'lucide-react';

export default function Settings() {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [journalLink, setJournalLink] = useState(null);
  const [loadingLink, setLoadingLink] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [tplForm, setTplForm] = useState({ id: null, title: '', body: '' });
  const [savingTpl, setSavingTpl] = useState(false);
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/reply-templates');
      setTemplates(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);
  const handleSaveTemplate = async () => {
    if (!tplForm.title.trim() || !tplForm.body.trim()) {
      toast.error('Titel und Text erforderlich'); return;
    }
    setSavingTpl(true);
    try {
      if (tplForm.id) {
        await api.put('/reply-templates/' + tplForm.id, { title: tplForm.title, body: tplForm.body });
        toast.success('Vorlage aktualisiert');
      } else {
        await api.post('/reply-templates', { title: tplForm.title, body: tplForm.body });
        toast.success('Vorlage erstellt');
      }
      setTplForm({ id: null, title: '', body: '' });
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSavingTpl(false);
    }
  };
  const handleEditTemplate = (t) => {
    setTplForm({ id: t.id, title: t.title, body: t.body });
  };
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    try {
      await api.delete('/reply-templates/' + id);
      toast.success('Vorlage gelöscht');
      fetchTemplates();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const fetchJournalLink = useCallback(async () => {
    try {
      const res = await api.get('/journal-magic');
      setJournalLink(res.data.link);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLink(false);
    }
  }, []);

  useEffect(() => {
    fetchJournalLink();
    fetchTemplates();
  }, [fetchJournalLink, fetchTemplates]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/ninja/sync');
      toast.success(res.data.message);
      setLastSync(new Date().toLocaleString('de-DE'));
    } catch (err) {
      toast.error('Fehler beim Synchronisieren');
    } finally {
      setSyncing(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password) {
      toast.error('Bitte alle Felder ausfüllen'); return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Neue Passwörter stimmen nicht überein'); return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('Neues Passwort muss mindestens 8 Zeichen haben'); return;
    }
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('Passwort erfolgreich geändert');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Ändern');
    } finally {
      setSavingPw(false);
    }
  };

  const handleGenerateLink = async () => {
    try {
      const res = await api.post('/journal-magic');
      setJournalLink(res.data.link);
      toast.success('Magic Link generiert');
    } catch (err) {
      toast.error('Fehler beim Generieren');
    }
  };

  const handleDeleteLink = async () => {
    if (!window.confirm('Magic Link wirklich löschen?')) return;
    try {
      await api.delete('/journal-magic');
      setJournalLink(null);
      toast.success('Magic Link gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/journal-entry/${journalLink.token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link kopiert');
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Einstellungen</h2>
        <p className="text-gray-500 text-sm mt-1">Systemkonfiguration und Integrationen</p>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Monitor size={16} className="text-blue-500" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">NinjaOne Integration</h3>
              <p className="text-xs text-gray-500">Geräte aus NinjaOne synchronisieren</p>
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Letzte Synchronisierung</p>
                <p className="text-xs text-gray-400 mt-0.5">{lastSync || 'Noch nicht synchronisiert'}</p>
              </div>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
              >
                <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Synchronisiert...' : 'Jetzt synchronisieren'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
              <Lock size={16} className="text-teal-500" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">Passwort ändern</h3>
              <p className="text-xs text-gray-500">Eigenes Passwort aktualisieren</p>
            </div>
          </div>
          <div className="p-5 space-y-4 max-w-md">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Aktuelles Passwort</label>
              <input
                type="password"
                value={pwForm.current_password}
                onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Neues Passwort</label>
              <input
                type="password"
                value={pwForm.new_password}
                onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Neues Passwort bestätigen</label>
              <input
                type="password"
                value={pwForm.confirm_password}
                onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={handlePasswordChange}
              disabled={savingPw}
              className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
            >
              {savingPw ? 'Wird gespeichert...' : 'Passwort ändern'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <Link size={16} className="text-purple-500" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">Journal Magic Link</h3>
              <p className="text-xs text-gray-500">Schnelleingabe für Journal-Einträge ohne Login</p>
            </div>
          </div>
          <div className="p-5">
            {loadingLink ? (
              <p className="text-sm text-gray-400">Lädt...</p>
            ) : journalLink ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/journal-entry/${journalLink.token}`}
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-600 font-mono"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm text-gray-600 transition-all"
                  >
                    <Copy size={14} />
                    Kopieren
                  </button>
                </div>
                {journalLink.last_used_at && (
                  <p className="text-xs text-gray-400">
                    Zuletzt verwendet: {new Date(journalLink.last_used_at).toLocaleString('de-DE')}
                  </p>
                )}
                <button
                  onClick={handleDeleteLink}
                  className="flex items-center gap-1.5 text-red-500 hover:text-red-600 text-sm transition-all"
                >
                  <Trash2 size={14} />
                  Link löschen
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">Kein aktiver Magic Link vorhanden.</p>
                <button
                  onClick={handleGenerateLink}
                  className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all"
                >
                  <Plus size={14} />
                  Link generieren
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Plus size={18} className="text-primary-500" />
              Antwortvorlagen
            </h3>
            <p className="text-gray-500 text-sm mt-1">Textbausteine für wiederkehrende Arbeiten im Ticket-Kommentarfeld</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <input
                type="text"
                value={tplForm.title}
                onChange={e => setTplForm({ ...tplForm, title: e.target.value })}
                placeholder="Titel (z.B. Standardwartung)"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <textarea
                value={tplForm.body}
                onChange={e => setTplForm({ ...tplForm, body: e.target.value })}
                placeholder="Vorlagentext..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveTemplate}
                  disabled={savingTpl}
                  className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
                >
                  <Plus size={14} />
                  {tplForm.id ? 'Vorlage aktualisieren' : 'Vorlage hinzufügen'}
                </button>
                {tplForm.id && (
                  <button
                    onClick={() => setTplForm({ id: null, title: '', body: '' })}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-all"
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-50 border-t border-gray-100 pt-2">
              {templates.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">Noch keine Vorlagen angelegt.</p>
              ) : (
                templates.map(t => (
                  <div key={t.id} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{t.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">{t.body}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleEditTemplate(t)} className="text-gray-400 hover:text-primary-500 transition-all">
                        <RefreshCw size={14} />
                      </button>
                      <button onClick={() => handleDeleteTemplate(t.id)} className="text-gray-400 hover:text-red-500 transition-all">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
